import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import SembleBooking from "@/components/SembleBooking";
import { Reveal, Label } from "@/components/ui-bits";
import { ONE_OFF_FEES } from "@/data/pricing";

const BEFORE_YOU_BOOK = [
  "Choose the assessment that fits: screening test, comprehensive panel or a clinical consultation. Fees are shown before you confirm.",
  "Blood tests are collected in the morning, ideally fasting. Booking slots reflect this.",
  "Booking an assessment does not mean treatment will be prescribed. TRT is not suitable for everyone.",
  "Already on TRT elsewhere? Choose the transfer assessment and have your recent results and prescription history to hand.",
];

export default function BookPage() {
  return (
    <>
      <Seo
        title="Book an Assessment | Androgen Health"
        description="Book a testosterone screening test, comprehensive diagnostic panel or clinical consultation online. Fees shown before you confirm. Treatment is subject to clinical assessment."
      />
      <PageHero
        label="Book an assessment"
        title={
          <>
            Choose a time.
            <br />
            <span className="serif-it text-teal">Know the cost first.</span>
          </>
        }
        lede="Appointments and blood tests are booked securely through Semble, our clinical practice system. Every fee is displayed before you confirm, and nothing is charged for an enquiry."
      />

      <section className="pb-24 px-5 sm:px-8" data-testid="book-body">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8">
            <Reveal>
              <SembleBooking />
            </Reveal>
          </div>

          <aside className="lg:col-span-4 space-y-6">
            <Reveal delay={0.08}>
              <div className="card-line rounded-2xl p-7">
                <Label>Before you book</Label>
                <ul className="mt-5 space-y-4">
                  {BEFORE_YOU_BOOK.map((t, i) => (
                    <li key={t} className="flex gap-4">
                      <span className="font-mono text-teal text-sm pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                      <p className="text-bone/85 text-sm leading-relaxed">{t}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.14}>
              <div className="card-line rounded-2xl p-7">
                <Label>Assessment fees</Label>
                <ul className="mt-5 divide-y divide-line" data-testid="book-fees-list">
                  {ONE_OFF_FEES.map((f) => (
                    <li key={f.service} className="py-3 flex items-start justify-between gap-4">
                      <span className="text-sm text-bone/85">{f.service}</span>
                      <span className="font-display text-lg tracking-wide text-teal whitespace-nowrap">{f.fee}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-dusk mt-4 leading-relaxed">Proposed launch pricing. Confirmed in the booking flow before payment.</p>
                <Link to="/trt-cost" className="btn-ghost !py-2.5 !px-5 text-sm mt-5" data-testid="book-fees-link">
                  Full fee schedule
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <p className="text-xs text-dusk leading-relaxed">
                Prefer to ask a question first? <Link to="/contact" className="text-teal hover:underline" data-testid="book-contact-link">Send an enquiry</Link> — no payment is taken and no treatment is promised.
              </p>
            </Reveal>
          </aside>
        </div>
      </section>
    </>
  );
}
